import { Head, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  ClipboardList,
  BookOpen,
  Download,
  Calendar,
  Star,
  FileText,
  ChevronRight,
  User as UserIcon,
  Mail,
  Lock
} from "lucide-react"
import logo from "@/assets/logo_transparente.png"
import { PageLayout } from "@/components/VocationalTest/PageLayout"

interface ProductAccess {
  1: boolean
  2: boolean
  3: boolean
  4: boolean
}

interface TestResult {
  hasBasicTest: boolean
  hasAdvancedTest: boolean
  basicTestDate: string | null
  advancedTestDate: string | null
}

interface Props {
  user: {
    id: number
    name: string | null
    email: string
  }
  productAccess: ProductAccess
  testResults: TestResult
  offerActive: boolean
  flash?: {
    notice?: string
    alert?: string
  }
}

export default function Dashboard({ user, productAccess, testResults, offerActive, flash }: Props) {
  const hasAnyPurchase = Object.values(productAccess).some(Boolean)

  return (
    <>
      <Head title="Minha Conta - Orientação Profissional" />
      <PageLayout>
        <div className="w-full max-w-4xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={logo} alt="25Segundos" className="h-32" />
              <div>
                <h1 className="text-2xl font-bold text-title-blue">Minha Conta</h1>
                <p className="text-sm text-muted-foreground">Bem-vindo(a), {user.name || user.email}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => router.delete("/logout")}>
              Sair
            </Button>
          </div>

          {flash?.notice && (
            <div className="p-4 bg-green-100 text-green-800 rounded-lg text-sm">
              {flash.notice}
            </div>
          )}

          {flash?.alert && (
            <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
              {flash.alert}
            </div>
          )}

          {/* Info do usuário */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                Dados da Conta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Email:</span>
                <span>{user.email}</span>
              </div>
              {user.name && (
                <div className="flex items-center gap-2 text-sm">
                  <UserIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Nome:</span>
                    <span>{user.name}</span>
                  </div>
              )}
            </CardContent>
          </Card>

          {/* Teste Gratuito - sempre visível */}
          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Teste Vocacional Gratuito
              </CardTitle>
              <CardDescription>
                Descubra seu perfil vocacional com nosso teste baseado no Eneagrama
              </CardDescription>
            </CardHeader>
            <CardContent>
              {testResults.hasBasicTest ? (
                <div className="space-y-3">
                <p className="text-sm text-green-700">
                    Teste realizado em {testResults.basicTestDate}
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={() => router.visit("/minha-conta/testes/resultados/basico")}>
                      <FileText className="mr-2 h-4 w-4" />
                      Ver Resultado
                    </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => router.visit("/about-test")}>
                  Começar Teste Gratuito
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
            )}
          </CardContent>
        </Card>

          {/* Produtos Adquiridos */}
          {offerActive && hasAnyPurchase && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-title-blue">Seus Produtos</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Produto 1: Teste Avançado */}
                {productAccess[1] && (
                  <Card className="border-blue-200 bg-blue-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-700">
                        <Star className="w-5 h-5" />
                        Conteúdo Avançado do Perfil Vocacional
                      </CardTitle>
                      <CardDescription>
                        Acesso ao teste avançado e relatório detalhado
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      {testResults.hasAdvancedTest ? (
                        <div className="space-y-3">
                          <p className="text-sm text-green-700">
                            Teste avançado realizado em {testResults.advancedTestDate}
                          </p>
                        <div className="flex gap-2">
                            <Button onClick={() => router.visit("/minha-conta/testes/resultados/avancado")}>
                              <FileText className="mr-2 h-4 w-4" />
                              Ver Resultado Avançado
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button onClick={() => router.visit("/advanced-welcome")}>
                          Iniciar Teste Avançado
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Produto 2: SEMEAR */}
                {productAccess[2] && (
                  <Card className="border-purple-200 bg-purple-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-purple-700">
                        <BookOpen className="w-5 h-5" />
                        Combo S.E.M.E.A.R (Curso + Livro)
                      </CardTitle>
                      <CardDescription>
                        Acesso ao curso completo e material do Método SEMEAR
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <p className="text-sm text-muted-foreground mb-3">
                        Informações de acesso serão enviadas por e-mail em breve.
                      </p>
                      <Button variant="outline" disabled>
                        <Lock className="mr-2 h-4 w-4" />
                        Acesso em breve
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Produto 3: E-book */}
                {productAccess[3] && (
                  <Card className="border-orange-200 bg-orange-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-orange-700">
                        <BookOpen className="w-5 h-5" />
                        Livro Digital - O Eneagrama que Transforma
                      </CardTitle>
                      <CardDescription>
                        E-book completo com 233 páginas sobre Eneagrama e Método SEMEAR
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <Button
                        onClick={async () => {
                          const response = await fetch("/downloads/ebook")
                          const blob = await response.blob()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement("a")
                          a.href = url
                          a.download = "O-Eneagrama-que-Transforma.pdf"
                          document.body.appendChild(a)
                          a.click()
                          document.body.removeChild(a)
                          window.URL.revokeObjectURL(url)
                        }}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Baixar e-book (PDF)
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Produto 4: Mentoria */}
                {productAccess[4] && (
                  <Card className="border-yellow-200 bg-yellow-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-yellow-700">
                        <Calendar className="w-5 h-5" />
                        Mentoria Individual - 10 Sessões
                      </CardTitle>
                      <CardDescription>
                        Acompanhamento com Rafaela Ferreira
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <Button onClick={() => router.visit("/mentoria")}>
                        <Calendar className="mr-2 h-4 w-4" />
                        Agendar Sessão
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Compre também */}
          {offerActive && hasAnyPurchase && !Object.values(productAccess).every(Boolean) && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-title-blue">Compre também</h2>
              <p className="text-sm text-muted-foreground">
                Veja o que ainda falta para completar sua jornada.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!productAccess[1] && (
                  <Card className="border-blue-200 bg-blue-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-700">
                        <Star className="w-5 h-5" />
                        Conteúdo Avançado do Perfil Vocacional
                      </CardTitle>
                      <CardDescription>
                        Acesso ao teste avançado e relatório detalhado
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <Button onClick={() => router.visit("/checkout")}>
                        Comprar agora
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {!productAccess[2] && (
                  <Card className="border-purple-200 bg-purple-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-purple-700">
                        <BookOpen className="w-5 h-5" />
                        Combo S.E.M.E.A.R (Curso + Livro)
                      </CardTitle>
                      <CardDescription>
                        Acesso ao curso completo e material do Método SEMEAR
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <Button onClick={() => router.visit("/checkout")}>
                        Comprar agora
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {!productAccess[3] && (
                  <Card className="border-orange-200 bg-orange-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-orange-700">
                        <BookOpen className="w-5 h-5" />
                        Livro Digital - O Eneagrama que Transforma
                      </CardTitle>
                      <CardDescription>
                        E-book completo com 233 páginas sobre Eneagrama e Método SEMEAR
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <Button onClick={() => router.visit("/checkout")}>
                        Comprar agora
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {!productAccess[4] && (
                  <Card className="border-yellow-200 bg-yellow-50/50 min-h-[220px] h-full flex flex-col">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-yellow-700">
                        <Calendar className="w-5 h-5" />
                        Mentoria Individual - 10 Sessões
                      </CardTitle>
                      <CardDescription>
                        Acompanhamento com Rafaela Ferreira
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-end">
                      <Button onClick={() => router.visit("/checkout")}>
                        Comprar agora
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Link para loja */}
          {offerActive && !hasAnyPurchase && (
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader>
                <CardTitle className="text-amber-800">Amplie sua jornada de autoconhecimento</CardTitle>
                <CardDescription className="text-amber-700">
                  Descubra nossos conteúdos exclusivos para aprofundar seu perfil vocacional
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Conteúdo Avançado</strong> - Teste exclusivo + relatório detalhado</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <BookOpen className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span><strong>E-book Eneagrama</strong> - 233 páginas de autoconhecimento</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Mentoria Individual</strong> - 10 sessões com Rafaela Ferreira</span>
                  </li>
                </ul>
                <Button onClick={() => router.visit("/checkout")} className="w-full bg-amber-600 hover:bg-amber-700">
                  Ver Todos os Produtos
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          )}

        </div>
      </PageLayout>
    </>
  )
}
